import { Container, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Info } from "@material-ui/icons";
import DoneIcon from "@material-ui/icons/Done";
import EditIcon from "@material-ui/icons/Edit";
import { App, Exercises, Workouts } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { metricConverter, useStateValue } from "../../config/AppContext";
import { triggerDebuggerAndThrow } from "../../utils/debugging-utils";
import {
  SuperSetForm as AddExercises,
  SuperSetForm as AddSuperset,
  SuperSetForm as EditExercise,
} from "../Modals";
import FaB from "../Utilities/FloatingActionButton";
import ExerciseSets from "./ExerciseSets";
import ExerciseSetsList from "./ExerciseSetsList";

// import { SectionProps } from './Details';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "0 20px",
    "& .MuiTypography-h5": {
      fontSize: 16,
      fontWeight: 600,
      color: "#525252",
      marginBottom: 35,
    },
    "& .no-sets-added": {
      display: "flex",
      flexDirection: "column",
      margin: "auto",
      padding: "60px 80px",
      height: "30vh",
      justifyContent: "center",
      textAlign: "center",
      // textTransform: 'capitalize',
      fontSize: 16,
      fontWeight: 500,
      color: "#595959",
      "& div": {
        width: "100%",
        "& svg": {
          height: 32,
          width: 32,
          fill: "#333333",
        },
      },
    },
  },
  spaceBtw: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  editText: {
    color: "var(--text-primary)",
    cursor: "pointer",
    marginBottom: 35,
  },
  saveBtn: {
    border: "none",
    color: "var(--text-primary)",
    backgroundColor: "inherit",
  },
}));

const SupersetSection = (props: any) => {
  const classes = useStyles();
  const { workout, session, dispatch, editSup } = props;
  const { t } = useTranslation();
  const { metric } = useStateValue() as { metric: App.State["metric"] };
  const [supersetList, setSupersetList] = React.useState<Workouts.IExercise[] | null>(null);
  const [addExercise, setAddExercise] = React.useState<Workouts.IExercise[] | null>(null);
  const [editExercise, setEditExercise] = React.useState<{
    exercise: Workouts.IExercise;
    exerciseIndex: number;
    supersetIndex: number;
  } | null>(null);

  const [toggleAddSuperset, setToggleAddSuperset] = React.useState(false);
  const [toggleAddExercise, setToggleAddExercise] = React.useState(false);
  const [toggleEditExercise, setToggleEditExercise] = React.useState(false);
  const [show, setShow] = React.useState(false);

  // Callbacks
  const showAddSupersetModal = React.useCallback(() => {
    if (!supersetList) return setToggleAddSuperset(false);
    return setToggleAddSuperset(true);
  }, [supersetList]);
  const showAddExerciseModal = React.useCallback(() => {
    if (!addExercise) return setToggleAddExercise(false);
    return setToggleAddExercise(true);
  }, [addExercise]);
  const showEditExerciseModal = React.useCallback(() => {
    if (!editExercise) return setToggleEditExercise(false);
    if (editExercise) return setToggleEditExercise(true);
  }, [editExercise]);
  // Effects
  React.useEffect(showAddSupersetModal);
  React.useEffect(showAddExerciseModal);
  React.useEffect(showEditExerciseModal);

  const generateSuperSetList = (
    accum: JSX.Element[],
    superset: Workouts.IExerciseSuperSet,
    supersetIndex: number
  ) => {
    const exerciseList = superset.exercises.map(
      (exercise, exerciseIndex): JSX.Element => (
        <ExerciseSets
          key={exerciseIndex}
          supersetIndex={supersetIndex}
          exerciseIndex={exerciseIndex}
          exercise={exercise}
          setEditExercise={setEditExercise}
        />
      )
    );
    accum = [
      ...accum,
      <ExerciseSetsList
        key={supersetIndex}
        superset={superset}
        supersetIndex={supersetIndex}
        exerciseList={exerciseList}
        session={session}
      />,
    ];
    return accum;
  };

  const generateWorkoutSection = () =>
    workout.exercise_super_set.length > 0 ? (
      workout.exercise_super_set.reduce(generateSuperSetList, [])
    ) : (
      <div className="no-sets-added">
        <div>
          <Info />
        </div>
        {/* {t("No exercises added yet. Please click the plus button to start adding exercises")} */}
        {t("No exercises added yet")}
      </div>
    );
  // Add Single Superset with Multiple Exercises
  const addSuperSetList = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const newSuperSetList = new FormData(e.currentTarget).get("exercises");
    if (!supersetList || !dispatch) return;
    if (newSuperSetList && typeof newSuperSetList === "string") {
      const newSuperSet = {
        id: uuid(),
        exercises: JSON.parse(newSuperSetList).map((exercise: Exercises.IExercise) => ({
          ...exercise,
          id: uuid(),
        })),
      };
      workout.exercise_super_set.push(newSuperSet);
      dispatch({
        type: "add",
        payload: workout,
      });
    }
    return setSupersetList(null);
  };
  // Add Multiple Exercises to Workout
  const addExercises = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!addExercise || !dispatch) return;
    const formData = new FormData(e.currentTarget).get("exercises");
    if (formData && typeof formData === "string") {
      const newExercises = [...JSON.parse(formData)];
      newExercises.forEach((exercise) => {
        exercise.id = uuid();
        workout.exercise_super_set.push({
          id: uuid(),
          exercises: [exercise],
        });
      });
      dispatch({
        type: "add",
        payload: workout,
      });
    }
    return setAddExercise(null);
  };
  // Update single Exercise
  const updateExerciseSet = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!editExercise || !dispatch) return setEditExercise(null);
    const currentExerciseSet = editExercise.exercise;
    // Collect input fields
    const formInputs = [
      ...e.currentTarget.querySelectorAll("input.form-control"),
    ] as HTMLInputElement[];
    // console.log('formInputs', formInputs)
    if (formInputs.length === 1) {
      const scoreTy = document.getElementsByName("score_type")[0] as HTMLSelectElement;
      const x = document.getElementById(
        `set-table-${editExercise.exerciseIndex + 1}`
      ) as HTMLTableElement;
      // ?.rows[1].cells[0].innerHTML
      let arr = scoreTy.selectedIndex ? ["REPS"] : ["TIME"];
      // console.log('row',x )
      if (x?.rows) {
        for (let i = 1; i < x.rows.length; i++) {
          if (i === 1) {
          } else {
            let j = x.rows[i].cells[0].children[0].innerHTML.split("<");
            // console.log('j', j[0])
            if (j[0] !== "Results") {
              arr.push(j[0].toUpperCase());
            }
          }
        }
      }
      // console.log('x', arr)
      currentExerciseSet.score_type = `${scoreTy.selectedIndex ? "REPS" : "TIME"}`;
      currentExerciseSet.score_vals = [];
      currentExerciseSet.score_cols = arr;
      if (formInputs[0].name === "exercise_notes") {
        const exercise_notes = formInputs[0].getAttribute("value");
        currentExerciseSet.exercise_notes = exercise_notes || null;
      }

      dispatch({
        type: "update-exercise",
        payload: {
          exercise: currentExerciseSet,
          exerciseIndex: editExercise.exerciseIndex,
          supersetIndex: editExercise.supersetIndex,
        },
      });
    } else {
      const initialValues =
        // currentExerciseSet.score_count &&
        {
          name: formInputs[0].name,
          value: +formInputs[0].value,
        };
      // console.log('after here', formInputs[0], initialValues)
      // console.log('initialValues', initialValues, currentExerciseSet)
      const newEntry = initialValues
        ? {
            id: uuid(),
            exercise_id: currentExerciseSet.exercise_id,
            score_count: 0,
            score_type: initialValues.name,
            score_cols: [initialValues.name],
            score_vals: [
              {
                type: initialValues.name,
                value: [metricConverter(initialValues.value, `${initialValues.name}O`, metric)],
              },
            ],
            result_vals: [],
            notes_sets: [],
            exercise_notes: null,
          }
        : {
            id: uuid(),
            exercise_id: currentExerciseSet.exercise_id,
            score_count: 0,
            score_type: currentExerciseSet.score_type,
            score_cols: [`${currentExerciseSet.score_type}`],
            score_vals: [{ type: `${currentExerciseSet.score_type}`, value: [] }],
            result_vals: [],
            notes_sets: [],
            exercise_notes: null,
          };
      const updatedExerciseSet = formInputs.reduce(
        (accum: Workouts.IExercise, formInput: HTMLInputElement) => {
          const targetData = formInput.parentElement as HTMLTableDataCellElement | null;
          if (!targetData) return accum;
          const setIndex = targetData.cellIndex - 1;
          // console.log('hi',formInput, targetData.cellIndex )
          // if input name is same as score type, update score vals:
          if (formInput.name === accum.score_type) {
            accum.score_vals[0].value[accum.score_count] = metricConverter(
              +formInput.value,
              `${formInput.name}O`,
              metric
            );
            accum.score_count = accum.score_count + 1;
            return accum;
          }
          // else if input name is exercise_notes update exercise_notes:
          else if (formInput.name === "exercise_notes") {
            const exercise_notes = formInput.getAttribute("value");
            if (!exercise_notes) return accum;
            else {
              accum.exercise_notes = exercise_notes;
              return accum;
            }
          }
          // else if input name is notes_sets update notes_sets:
          else if (formInput.name === "notes_sets") {
            const notes_set = formInput.getAttribute("value");
            if (!notes_set) {
              return accum;
            } else {
              accum.notes_sets[setIndex] = notes_set;
              return accum;
            }
          }

          // else if input name is result_vals update result_vals:
          else if (formInput.name === "result_vals") {
            accum.result_vals[setIndex] = metricConverter(
              +formInput.value,
              `${accum.score_type}O`,
              metric
            );
            return accum;
          }

          // else if input name exists in score cols update score values:
          else if (accum.score_cols.find((score) => score === formInput.name)) {
            accum.score_vals[accum.score_cols.findIndex((score) => score === formInput.name)].value[
              setIndex
            ] = metricConverter(+formInput.value, `${formInput.name}O`, metric);
            return accum;
          }

          // else input name doesn't exist, add score col and score val:
          else {
            accum.score_cols.push(formInput.name);
            accum.score_vals.push({
              type: formInput.name,
              value: [metricConverter(+formInput.value, `${formInput.name}O`, metric)],
            });
            return accum;
          }
        },
        newEntry
      );
      console.log("updated", updatedExerciseSet);
      //  updatedExerciseSet.score_cols.length===1 && updatedExerciseSet.score_cols.push("PAUSE") && updatedExerciseSet.score_vals.push({type:"PAUSE", value:[0]})
      dispatch({
        type: "update-exercise",
        payload: {
          exercise: updatedExerciseSet,
          exerciseIndex: editExercise.exerciseIndex,
          supersetIndex: editExercise.supersetIndex,
        },
      });
    }
    return setEditExercise(null);
  };

  return (
    <Container className={classes.container}>
      <div className={classes.spaceBtw}>
        {!!editSup ? (
          !editSup[0] ? (
            <Typography variant="h5">{t("View workout info")}</Typography>
          ) : (
            <Typography variant="h5">
              {/* {t("Exercise Edit Section")} */}
              {t("Exercises")}
            </Typography>
          )
        ) : (
          <Typography variant="h5">{t("View workout info")}</Typography>
        )}
        {!!editSup &&
          (!editSup[0] ? (
            <div className={classes.editText} onClick={(_) => editSup[1](true)}>
              <EditIcon fontSize="small" />
              {t("Edit")}
            </div>
          ) : (
            <button
              type="button"
              className={`${classes.saveBtn} ${classes.editText}`} //disabled={loading}
              onClick={(_) => editSup[1](false)}
            >
              <DoneIcon fontSize="small" />
              {t("Done")}{" "}
            </button>
          ))}
      </div>
      {!!editSup
        ? !editSup[0]
          ? generateWorkoutSection()
          : generateWorkoutSection()
        : generateWorkoutSection()}

      <AddSuperset
        show={toggleAddSuperset}
        option={"Add"}
        supersetList={supersetList}
        onSubmit={addSuperSetList}
        onHide={() => setSupersetList(null)}
      />
      <AddExercises
        show={toggleAddExercise}
        option={"Add Exercises"}
        addExercise={addExercise}
        onSubmit={addExercises}
        onHide={() => setAddExercise(null)}
      />
      <EditExercise
        show={toggleEditExercise}
        option={"Edit Exercise"}
        editExercise={editExercise}
        onSubmit={updateExerciseSet}
        onHide={() => setEditExercise(null)}
      />

      <FaB
        show={show}
        onClick={() => setShow(!show)}
        options={[`${t("Add Exercises")}`, `${t("Add")} ${t("superset")}`]}
        onAddSuperset={() => setSupersetList([])}
        onAddExercise={() => setAddExercise([])}
        onAdd={triggerDebuggerAndThrow}
        onInvite={triggerDebuggerAndThrow}
      />
    </Container>
  );
};
export default SupersetSection;
